import type { StatcastSeasonRow } from './sabermetrics';
/**
 * API クライアント（Next.js 用）
 * - import.meta.env.VITE_API_BASE_URL → process.env.NEXT_PUBLIC_API_BASE_URL
 * - window.location.hostname チェックはそのまま維持
 */

import type {
  Profile, Team, MlbTeam, BatterStats, PitcherStats, DefenseStats,
  PlayerSearchItem, PlayerSearchResponse, PlayerAward, AwardsResponse,
  AwardWinnersResponse, PlayerNpbAward, NpbAwardsResponse, NpbAwardWinnersResponse,
  TraitRankingResponse, PlayerTrait, InternationalPlayerStatsResponse,
  InternationalBatterStats, InternationalPitcherStats, InternationalCompetition,
  InternationalCompetitionDetail, InternationalCompetitionPlayerStatsResponse,
  InternationalGame, InternationalTeam,
  InternationalTeamStatsRow,
  InternationalLeaderRow, InternationalGameBatterStats, InternationalGamePitcherStats,
  InternationalGameStatsResponse,
  CareerOrganizationMeta, CareerOrganizationPlayersResponse,
} from './types';

export type {
  Profile, Team, MlbTeam, BatterStats, PitcherStats, DefenseStats,
  PlayerSearchItem, PlayerSearchResponse, PlayerAward, AwardsResponse,
  AwardWinnersResponse, PlayerNpbAward, NpbAwardsResponse, NpbAwardWinnersResponse,
  TraitRankingResponse, PlayerTrait, InternationalPlayerStatsResponse,
  InternationalBatterStats, InternationalPitcherStats, InternationalCompetition,
  InternationalCompetitionDetail, InternationalCompetitionPlayerStatsResponse,
  InternationalGame, InternationalTeam,
  InternationalTeamStatsRow,
  InternationalLeaderRow, InternationalGameBatterStats, InternationalGamePitcherStats,
  InternationalGameStatsResponse,
  CareerOrganizationMeta, CareerOrganizationPlayersResponse,
};

export type PitcherCareerComparisonOperator = 'gte' | 'lte';
export type PitcherCareerFilterCondition = { op: PitcherCareerComparisonOperator; value: number };
export type DefenseFilterScope = 'total' | 'position';
export type PlayerSearchFilters = {
  active?: 0 | 1;
  ob?: 0 | 1;
  pos?: string;
  team?: string;
  pitch?: string;
  bat?: string;
  nationality?: string;
  first_year?: number;
  last_year?: number;
  pitcher_career?: Partial<Record<string, PitcherCareerFilterCondition>>;
  batter_career?: Partial<Record<string, PitcherCareerFilterCondition>>;
  defense_career?: Partial<Record<string, PitcherCareerFilterCondition>>;
  pitcher_season_year?: number;
  pitcher_season?: Partial<Record<string, PitcherCareerFilterCondition>>;
  batter_season_year?: number;
  batter_season?: Partial<Record<string, PitcherCareerFilterCondition>>;
  defense_season_year?: number;
  defense_season?: Partial<Record<string, PitcherCareerFilterCondition>>;
  defense_scope?: DefenseFilterScope;
  defense_position?: string;
};

// サーバーサイド（ビルド時）は API_BASE_URL を優先（ローカルバックエンドで高速化）
// クライアント側は NEXT_PUBLIC_API_BASE_URL を使用し、未設定時は本番サイト自身を基準にする
const DEFAULT_PUBLIC_API_BASE_URL = 'https://baseballdataviewer.com';
const API_BASE_URL =
  (typeof window === 'undefined' ? process.env.API_BASE_URL : undefined)
  ?? process.env.NEXT_PUBLIC_API_BASE_URL
  ?? DEFAULT_PUBLIC_API_BASE_URL;
const BUILD_API_DEBUG = typeof window === 'undefined' && process.env.BUILD_API_DEBUG === '1';
const MAX_API_CACHE_ENTRIES = 200;
const parsePositiveInt = (raw: string | undefined, fallback: number): number => {
  const parsed = Number.parseInt((raw ?? '').trim(), 10);
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback;
  return parsed;
};
const parseNonNegativeInt = (raw: string | undefined, fallback: number): number => {
  const parsed = Number.parseInt((raw ?? '').trim(), 10);
  if (!Number.isFinite(parsed) || parsed < 0) return fallback;
  return parsed;
};
const BUILD_API_TIMEOUT_MS = parsePositiveInt(process.env.BUILD_API_TIMEOUT_MS, 30_000);
const BUILD_API_MAX_RETRIES = parseNonNegativeInt(process.env.BUILD_API_MAX_RETRIES, 2);
const BUILD_API_RETRY_BASE_MS = parsePositiveInt(process.env.BUILD_API_RETRY_BASE_MS, 300);
const BUILD_API_MAX_INFLIGHT = parsePositiveInt(process.env.BUILD_API_MAX_INFLIGHT, 8);
const ENABLE_BUILD_FS_CACHE =
  typeof window === 'undefined' && process.env.NODE_ENV === 'production';
const ENABLE_RUNTIME_API_CACHE = process.env.NODE_ENV === 'production';
const BUILD_API_METRICS_FILE =
  typeof window === 'undefined' ? (process.env.BDV_BUILD_API_METRICS_FILE ?? '').trim() : '';

const debugBuildApi = (...args: unknown[]): void => {
  if (!BUILD_API_DEBUG) return;
  console.log('[build-api]', ...args);
};

type BuildApiMetricEvent = {
  event: 'cache_hit_memory' | 'cache_hit_storage' | 'cache_hit_build' | 'request_success' | 'request_retry' | 'request_failed';
  url: string;
  status?: number;
  attempt?: number;
  elapsedMs?: number;
  message?: string;
};

let buildApiMetricsContextPromise: Promise<{
  fs: typeof import('node:fs/promises');
  path: typeof import('node:path');
  file: string;
} | null> | null = null;

const getBuildApiMetricsContext = async () => {
  if (!BUILD_API_METRICS_FILE) return null;
  if (!buildApiMetricsContextPromise) {
    buildApiMetricsContextPromise = (async () => {
      try {
        const dynamicImport = getDynamicImport();
        if (!dynamicImport) return null;
        const [fs, path] = await Promise.all([
          dynamicImport('node:fs/promises') as Promise<typeof import('node:fs/promises')>,
          dynamicImport('node:path') as Promise<typeof import('node:path')>,
        ]);
        const file = path.resolve(BUILD_API_METRICS_FILE);
        await fs.mkdir(path.dirname(file), { recursive: true });
        return { fs, path, file };
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : String(error);
        debugBuildApi('metrics_setup_failed', { message });
        return null;
      }
    })();
  }
  return buildApiMetricsContextPromise;
};

const appendBuildApiMetric = async (event: BuildApiMetricEvent): Promise<void> => {
  const context = await getBuildApiMetricsContext();
  if (!context) return;
  try {
    const payload = {
      ...event,
      pid: process.pid,
      at: new Date().toISOString(),
    };
    await context.fs.appendFile(context.file, `${JSON.stringify(payload)}\n`, 'utf-8');
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : String(error);
    debugBuildApi('metrics_write_failed', { message });
  }
};

// -------------------------------------------------------
// ビルド時ファイルシステムキャッシュ（サーバーサイド専用）
// Next.js Data Cache は 2MB 超のレスポンスを保存できないため独自キャッシュを使用
// require() をサーバーサイド条件内で呼ぶことでクライアントバンドルへの混入を防ぐ
// ワーカープロセスをまたいで共有されるため、同一 URL の重複フェッチを削減する
// -------------------------------------------------------

type DynamicImportLike = (specifier: string) => Promise<unknown>;
type BuildCacheContext = {
  fs: typeof import('node:fs/promises');
  path: typeof import('node:path');
  crypto: typeof import('node:crypto');
  dir: string;
};

const getDynamicImport = (): DynamicImportLike | null => {
  if (typeof window !== 'undefined') return null;
  try {
    return new Function('specifier', 'return import(specifier);') as DynamicImportLike;
  } catch {
    return null;
  }
};

let buildCacheContextPromise: Promise<BuildCacheContext | null> | null = null;

const getBuildCacheContext = async (): Promise<BuildCacheContext | null> => {
  if (!ENABLE_BUILD_FS_CACHE) return null;
  if (!buildCacheContextPromise) {
    buildCacheContextPromise = (async () => {
      try {
        const dynamicImport = getDynamicImport();
        if (!dynamicImport) return null;
        const [fs, path, crypto] = await Promise.all([
          dynamicImport('node:fs/promises') as Promise<typeof import('node:fs/promises')>,
          dynamicImport('node:path') as Promise<typeof import('node:path')>,
          dynamicImport('node:crypto') as Promise<typeof import('node:crypto')>,
        ]);
        const customRoot = (process.env.BUILD_CACHE_DIR ?? '').trim();
        const dir = customRoot
          ? path.resolve(customRoot, 'bdv-api')
          : path.resolve(process.cwd(), '.bdv-build-cache', 'bdv-api');
        await fs.mkdir(dir, { recursive: true });
        debugBuildApi('cache_dir', { dir });
        return { fs, path, crypto, dir };
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : String(error);
        debugBuildApi('cache_setup_failed', { message });
        return null;
      }
    })();
  }
  return buildCacheContextPromise;
};

const getBuildCacheFile = async (
  url: string,
): Promise<{ context: BuildCacheContext; file: string } | null> => {
  const context = await getBuildCacheContext();
  if (!context) return null;
  const key = context.crypto.createHash('md5').update(url).digest('hex');
  return { context, file: context.path.join(context.dir, `${key}.json`) };
};

const readBuildCache = async (url: string): Promise<unknown | null> => {
  const resolved = await getBuildCacheFile(url);
  if (!resolved) return null;
  try {
    const raw = await resolved.context.fs.readFile(resolved.file, 'utf-8');
    return JSON.parse(raw);
  } catch (error: unknown) {
    const code = typeof error === 'object' && error && 'code' in error
      ? String((error as { code?: unknown }).code)
      : '';
    if (code !== 'ENOENT') {
      const message = error instanceof Error ? error.message : String(error);
      debugBuildApi('cache_read_failed', { file: resolved.file, message });
    }
    return null;
  }
};

const writeBuildCache = async (url: string, data: unknown): Promise<void> => {
  const resolved = await getBuildCacheFile(url);
  if (!resolved) return;
  const serialized = JSON.stringify(data);
  const tmp = `${resolved.file}.${process.pid}.${Date.now()}.tmp`;
  try {
    // Write to a temporary file first, then rename.
    await resolved.context.fs.writeFile(tmp, serialized, 'utf-8');
    await resolved.context.fs.rename(tmp, resolved.file);
    debugBuildApi('cache_store_build', { file: resolved.file });
  } catch (error: unknown) {
    try {
      await resolved.context.fs.writeFile(resolved.file, serialized, 'utf-8');
      debugBuildApi('cache_store_build_fallback', { file: resolved.file });
    } catch (fallbackError: unknown) {
      const message = fallbackError instanceof Error ? fallbackError.message : String(fallbackError);
      debugBuildApi('cache_write_failed', { file: resolved.file, message });
    }
    const message = error instanceof Error ? error.message : String(error);
    debugBuildApi('cache_write_retry_direct', { file: resolved.file, message });
  } finally {
    try {
      await resolved.context.fs.unlink(tmp);
    } catch {
      // renamed or never created
    }
  }
};
const MAX_PERSISTENT_CACHE_ENTRIES = 200;
const API_CACHE_TTL_MS = 30 * 60 * 1000;
const PERSISTENT_CACHE_KEY_PREFIX = 'bdv:api-cache:';

const DISABLE_PERSISTENT_API_CACHE = (() => {
  if (typeof window === 'undefined') return false;
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1';
})();

const apiResponseCache = new Map<string, unknown>();

type PersistentCacheEntry = { expiresAt: number; touchedAt: number; data: unknown };

const cloneJson = <T>(v: T): T => JSON.parse(JSON.stringify(v)) as T;

const getPersistentStorage = (type: 'session' | 'local'): Storage | null => {
  if (DISABLE_PERSISTENT_API_CACHE || typeof window === 'undefined') return null;
  try { return type === 'session' ? window.sessionStorage : window.localStorage; }
  catch { return null; }
};

const parsePersistentEntry = (raw: string | null): PersistentCacheEntry | null => {
  if (!raw) return null;
  try {
    const p = JSON.parse(raw) as Partial<PersistentCacheEntry>;
    if (typeof p.expiresAt !== 'number' || typeof p.touchedAt !== 'number' || !('data' in p)) return null;
    return p as PersistentCacheEntry;
  } catch { return null; }
};

const prunePersistentStorage = (s: Storage) => {
  const now = Date.now();
  const alive: { key: string; touchedAt: number }[] = [];
  const remove: string[] = [];
  for (let i = 0; i < s.length; i++) {
    const k = s.key(i);
    if (!k?.startsWith(PERSISTENT_CACHE_KEY_PREFIX)) continue;
    const e = parsePersistentEntry(s.getItem(k));
    if (!e || e.expiresAt <= now) { remove.push(k); continue; }
    alive.push({ key: k, touchedAt: e.touchedAt });
  }
  remove.forEach(k => s.removeItem(k));
  if (alive.length > MAX_PERSISTENT_CACHE_ENTRIES) {
    alive.sort((a, b) => b.touchedAt - a.touchedAt);
    alive.slice(MAX_PERSISTENT_CACHE_ENTRIES).forEach(({ key }) => s.removeItem(key));
  }
};

const saveToCache = (url: string, data: unknown) => {
  const payload: PersistentCacheEntry = { expiresAt: Date.now() + API_CACHE_TTL_MS, touchedAt: Date.now(), data };
  const serialized = JSON.stringify(payload);
  const cacheKey = PERSISTENT_CACHE_KEY_PREFIX + url;
  for (const t of ['session', 'local'] as const) {
    const s = getPersistentStorage(t);
    if (!s) continue;
    try { s.setItem(cacheKey, serialized); prunePersistentStorage(s); }
    catch { try { prunePersistentStorage(s); s.setItem(cacheKey, serialized); } catch { /* ignore */ } }
  }
};

const loadFromCache = (url: string): unknown | null => {
  const cacheKey = PERSISTENT_CACHE_KEY_PREFIX + url;
  const now = Date.now();
  for (const t of ['session', 'local'] as const) {
    const s = getPersistentStorage(t);
    if (!s) continue;
    try {
      const e = parsePersistentEntry(s.getItem(cacheKey));
      if (!e) { s.removeItem(cacheKey); continue; }
      if (e.expiresAt <= now) { s.removeItem(cacheKey); continue; }
      saveToCache(url, e.data);
      return e.data;
    } catch { /* ignore */ }
  }
  return null;
};

const touchEntry = (url: string, value: unknown) => {
  if (!ENABLE_RUNTIME_API_CACHE) return;
  if (apiResponseCache.has(url)) apiResponseCache.delete(url);
  apiResponseCache.set(url, value);
  while (apiResponseCache.size > MAX_API_CACHE_ENTRIES) {
    const oldest = apiResponseCache.keys().next().value as string | undefined;
    if (!oldest) break;
    apiResponseCache.delete(oldest);
  }
};

type ReleaseBuildApiSlot = (() => void) | null;
let buildApiInFlight = 0;
const buildApiWaiters: Array<() => void> = [];

const releaseBuildApiSlot = () => {
  buildApiInFlight = Math.max(0, buildApiInFlight - 1);
  const next = buildApiWaiters.shift();
  if (next) next();
};

const acquireBuildApiSlot = async (): Promise<ReleaseBuildApiSlot> => {
  if (typeof window !== 'undefined') return null;
  if (BUILD_API_MAX_INFLIGHT <= 0) return null;

  if (buildApiInFlight >= BUILD_API_MAX_INFLIGHT) {
    await new Promise<void>((resolve) => buildApiWaiters.push(resolve));
  }

  buildApiInFlight += 1;
  return releaseBuildApiSlot;
};

class ApiHttpError extends Error {
  readonly status: number;

  constructor(status: number, url: string) {
    super(`API Error: ${status} (${url})`);
    this.name = 'ApiHttpError';
    this.status = status;
  }
}

const sleep = async (ms: number) => {
  await new Promise<void>((resolve) => setTimeout(resolve, ms));
};

const isTransientError = (error: unknown): boolean => {
  if (error instanceof DOMException && error.name === 'AbortError') return false;
  if (error instanceof ApiHttpError) {
    return error.status === 429 || (error.status >= 500 && error.status <= 599);
  }
  if (error instanceof TypeError) return true;
  if (error instanceof Error) {
    const message = error.message.toLowerCase();
    return (
      message.includes('fetch failed') ||
      message.includes('econnrefused') ||
      message.includes('econnreset') ||
      message.includes('socket') ||
      message.includes('timed out') ||
      message.includes('timeout') ||
      message.includes('network')
    );
  }
  return false;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const apiFetch = async (path: string, signal?: AbortSignal): Promise<any> => {
  const fullPath = path.startsWith('/') ? path : `/${path}`;
  const url = API_BASE_URL + fullPath;
  const bypassCache = fullPath.startsWith('/api/international/');
  if (BUILD_API_DEBUG) debugBuildApi('request', { apiBaseUrl: API_BASE_URL, url });

  if (signal?.aborted) throw new DOMException('The operation was aborted.', 'AbortError');

  if (!bypassCache && ENABLE_RUNTIME_API_CACHE && apiResponseCache.has(url)) {
    const c = apiResponseCache.get(url);
    debugBuildApi('cache_hit_memory', { url });
    await appendBuildApiMetric({ event: 'cache_hit_memory', url });
    touchEntry(url, c);
    return cloneJson(c);
  }

  if (!bypassCache) {
    const cached = loadFromCache(url);
    if (cached !== null) {
      debugBuildApi('cache_hit_storage', { url });
      await appendBuildApiMetric({ event: 'cache_hit_storage', url });
      touchEntry(url, cached);
      return cloneJson(cached);
    }
  }

  // ビルド時: ファイルシステムキャッシュを確認（ワーカーをまたいで共有、2MB制限なし）
  if (typeof window === 'undefined' && !bypassCache) {
    const buildCached = await readBuildCache(url);
    if (buildCached !== null) {
      debugBuildApi('cache_hit_build', { url });
      await appendBuildApiMetric({ event: 'cache_hit_build', url });
      touchEntry(url, buildCached);
      return cloneJson(buildCached as unknown);
    }
  }

  // 静的ビルド互換のため no-store は使わない（output: 'export' + dynamic='error' で失敗する）
  // timeout: ハングアップ防止（30秒で AbortError → .catch(() => undefined) でフォールバック）
  const isServer = typeof window === 'undefined';
  const fetchInit: RequestInit = { signal };
  if (isServer && !signal) {
    fetchInit.signal = AbortSignal.timeout(BUILD_API_TIMEOUT_MS);
  }

  const maxRetries = isServer ? BUILD_API_MAX_RETRIES : 0;
  let attempt = 0;

  for (;;) {
    let releaseSlot: ReleaseBuildApiSlot = null;
    const startedAt = Date.now();
    try {
      releaseSlot = await acquireBuildApiSlot();
      const res = await fetch(url, fetchInit);
      if (!res.ok) throw new ApiHttpError(res.status, url);
      const data = await res.json();
      await appendBuildApiMetric({
        event: 'request_success',
        url,
        status: res.status,
        attempt,
        elapsedMs: Date.now() - startedAt,
      });
      if (!bypassCache) {
        touchEntry(url, data);
      }
      if (isServer && !bypassCache) {
        await writeBuildCache(url, data);
      } else if (!bypassCache) {
        saveToCache(url, data);
      }
      return cloneJson(data);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : String(error);
      if (attempt < maxRetries && isTransientError(error)) {
        const delayMs = BUILD_API_RETRY_BASE_MS * (2 ** attempt);
        debugBuildApi('request_retry', { url, attempt: attempt + 1, delayMs, message });
        await appendBuildApiMetric({
          event: 'request_retry',
          url,
          status: error instanceof ApiHttpError ? error.status : undefined,
          attempt: attempt + 1,
          elapsedMs: Date.now() - startedAt,
          message,
        });
        attempt += 1;
        await sleep(delayMs);
        continue;
      }
      debugBuildApi('request_failed', { url, attempt, message });
      await appendBuildApiMetric({
        event: 'request_failed',
        url,
        status: error instanceof ApiHttpError ? error.status : undefined,
        attempt,
        elapsedMs: Date.now() - startedAt,
        message,
      });
      throw error;
    } finally {
      if (releaseSlot) releaseSlot();
    }
  }
};

// -------------------------------------------------------
// 公開API関数
// -------------------------------------------------------

export const fetchTeams = async (signal?: AbortSignal): Promise<Team[]> => {
  try { const d = await apiFetch('/api/teams/npb', signal); return d.teams ?? []; }
  catch (e) { if (e instanceof Error && e.name === 'AbortError') throw e; return []; }
};

export const fetchMlbTeams = async (signal?: AbortSignal): Promise<{ year: number; teams: MlbTeam[] }> => {
  try { return await apiFetch('/api/teams/mlb', signal); }
  catch (e) { if (e instanceof Error && e.name === 'AbortError') throw e; return { year: 0, teams: [] }; }
};

export const fetchPlayerSearch = async (
  q: string,
  signal?: AbortSignal,
  filters?: PlayerSearchFilters,
  page = 1,
): Promise<PlayerSearchResponse> => {
  try {
    const p = new URLSearchParams();
    p.set('q', q);
    p.set('page', String(Math.max(1, Math.floor(page))));
    if (filters?.active !== undefined) p.set('active', String(filters.active));
    if (filters?.ob !== undefined) p.set('ob', String(filters.ob));
    if (filters?.pos) p.set('pos', filters.pos);
    if (filters?.team) p.set('team', filters.team);
    if (filters?.pitch) p.set('pitch', filters.pitch);
    if (filters?.bat) p.set('bat', filters.bat);
    if (filters?.nationality) p.set('nationality', filters.nationality);
    if (filters?.first_year !== undefined) p.set('first_year', String(filters.first_year));
    if (filters?.last_year !== undefined) p.set('last_year', String(filters.last_year));
    const appendCareer = (prefix: string, obj?: Partial<Record<string, PitcherCareerFilterCondition>>) => {
      if (!obj) return;
      for (const [k, c] of Object.entries(obj)) {
        if (!c || !Number.isFinite(c.value)) continue;
        p.set(`${prefix}_${k}`, String(c.value));
        p.set(`${prefix}_${k}_op`, c.op === 'lte' ? 'lte' : 'gte');
      }
    };
    appendCareer('pc', filters?.pitcher_career);
    appendCareer('bc', filters?.batter_career);
    appendCareer('dc', filters?.defense_career);
    if (filters?.pitcher_season_year !== undefined) p.set('ps_year', String(filters.pitcher_season_year));
    appendCareer('ps', filters?.pitcher_season);
    if (filters?.batter_season_year !== undefined) p.set('bs_year', String(filters.batter_season_year));
    appendCareer('bs', filters?.batter_season);
    if (filters?.defense_season_year !== undefined) p.set('ds_year', String(filters.defense_season_year));
    appendCareer('ds', filters?.defense_season);
    if (filters?.defense_scope) p.set('d_scope', filters.defense_scope === 'position' ? 'position' : 'total');
    if (filters?.defense_position) p.set('d_pos', filters.defense_position);
    const d = await apiFetch(`/api/players/search?${p.toString()}`, signal);
    return {
      q: d.q ?? q,
      results: d.results ?? [],
      total: typeof d.total === 'number' ? d.total : (d.results ?? []).length,
      page: typeof d.page === 'number' ? d.page : Math.max(1, Math.floor(page)),
      pageSize: typeof d.pageSize === 'number' ? d.pageSize : 20,
      totalPages: typeof d.totalPages === 'number'
        ? d.totalPages
        : Math.ceil((typeof d.total === 'number' ? d.total : (d.results ?? []).length) / 20),
    };
  } catch (e) {
    if (e instanceof Error && e.name === 'AbortError') throw e;
    return { q, results: [], total: 0, page: Math.max(1, Math.floor(page)), pageSize: 20, totalPages: 0 };
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapProfile = (p: any): Profile => ({
  id: p.profileId, profile_id: p.profileId, name: p.name, name_reading: p.nameReading ?? null,
  slug: p.slug ?? null, kana: '', team_id: 0, birthday: p.birthday ?? '', birthday_date: p.birthdayDate ?? null,
  death_date: p.deathDate ?? null, death_date_date: p.deathDateDate ?? null, nationality: p.nationality ?? null,
  birthplace: p.birthplace ?? '', height_weight: p.heightWeight ?? '', height_cm: p.heightCm ?? null,
  weight_kg: p.weightKg ?? null, position: p.position ?? '', main_position: p.main_position ?? p.mainPosition ?? null,
  pitch_bat: p.pitchBat ?? '', draft: p.draft ?? '', draft_en: p.draftEn ?? null, draft_es: p.draftEs ?? null,
  career: p.career ?? '', career_en: p.careerEn ?? null, career_es: p.careerEs ?? null,
  uniform_number: p.uniformNumber ?? p.uniform_number ?? '', salary: '', debut: p.debut ?? '', url: '', last_game: p.lastGame ?? null, page_id: 0,
  wikipedia_last_modified: p.wikipediaLastModified ?? '', last_updated: p.lastUpdated ?? null,
  name_en: p.nameEn ?? null, nationality_en: p.nationalityEn ?? null, nationality_es: p.nationalityEs ?? null,
  birthplace_en: p.birthplaceEn ?? null, birthplace_es: p.birthplaceEs ?? null,
  height_feet_inch: p.heightFeetInch ?? null, weight_pound: p.weightPound ?? null,
  position_en: p.positionEn ?? null, main_position_en: p.main_position_en ?? p.mainPositionEn ?? null,
  pitch: p.pitch ?? '', bat: p.bat ?? '', created_at: '', updated_at: '',
  career_organizations: p.careerOrganizations ?? [],
});

export const fetchCareerOrganizationPlayers = async (
  slug: string,
  selected: string[] = [],
  signal?: AbortSignal,
): Promise<CareerOrganizationPlayersResponse> => {
  const params = new URLSearchParams({ schema: '2' });
  for (const value of selected) params.append('organization', value);
  const data = await apiFetch(`/api/career-organizations/${encodeURIComponent(slug)}/players?${params.toString()}`, signal);
  return { ...data, players: (data.players ?? []).map(mapProfile) };
};

export const fetchCareerOrganizationPlayersBySelection = async (
  selected: string[],
  signal?: AbortSignal,
  roles: import('./careerOrganizationFilters').CareerRoleFilters = {},
): Promise<CareerOrganizationPlayersResponse> => {
  const params = new URLSearchParams({ schema: '2' });
  for (const value of selected) params.append('organization', value);
  for (const [category, mode] of Object.entries(roles)) params.append('role', `${category}:${mode}`);
  const data = await apiFetch(`/api/career-organizations/players?${params.toString()}`, signal);
  return { ...data, players: (data.players ?? []).map(mapProfile) };
};

export const fetchCareerOrganizations = async (q: string = '', signal?: AbortSignal): Promise<CareerOrganizationMeta[]> => {
  const params = new URLSearchParams({ limit: '30', schema: '2' });
  if (q.trim()) params.set('q', q.trim());
  return apiFetch(`/api/career-organizations?${params.toString()}`, signal);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapBatter = (r: any, pid: number): BatterStats => ({
  sabermetrics: r.sabermetrics ?? {},
  id: 0, profile_id: pid, year: r.year ? String(r.year) : '通算', round: r.round, league: r.league, team_id: 0,
  team_name: r.team, team_slug: r.team_slug, team_name_ja: r.team_name_ja, team_name_en: r.team_name_en,
  g: r.games, pa: r.plateAppearances, ab: r.atBats, r: r.runs, h: r.hits, no2: r.doubles, no3: r.triples,
  hr: r.homeRuns, tb: r.totalBases, rbi: r.rbi, sb: r.steals, cs: r.caughtStealing, sh: r.sacBunts,
  sf: r.sacFlies, bb: r.walks, ibb: r.intentionalWalks, hp: r.hitByPitch, so: r.strikeouts,
  dp: r.doublePlays, ba: parseFloat(r.avg) || 0, slg: parseFloat(r.slg) || 0, obp: parseFloat(r.obp) || 0,
  ops: parseFloat(r.ops) || 0, created_at: '', updated_at: '',
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapPitcher = (r: any, pid: number): PitcherStats => ({
  sabermetrics: r.sabermetrics ?? {},
  id: 0, profile_id: pid, year: r.year ? String(r.year) : '通算', round: r.round, league: r.league, team_id: 0,
  team_name: r.team, team_slug: r.team_slug, team_name_ja: r.team_name_ja, team_name_en: r.team_name_en,
  g: r.games, starts: r.starts, complete: r.complete, shutout: r.shutout, no_bb: r.noWalk ?? null,
  w: r.wins, l: r.losses, sv: r.saves, hld: r.holds ?? null, hp: r.holds, win_pct: parseFloat(r.winRate) || 0,
  bf: r.batters, ip: r.innings, h: r.hits, hr: r.homeRuns, bb: r.walks, ibb: r.intentionalWalks,
  hbp: r.hitByPitch, so: r.strikeouts, wp: r.wildPitches, bk: r.balks, r: r.runs, er: r.earnedRuns,
  era: parseFloat(r.era) || 0, whip: parseFloat(r.whip) || 0, created_at: '', updated_at: '',
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapDefense = (r: any, pid: number): DefenseStats => ({
  id: 0, profile_id: pid, year: r.year ? String(r.year) : (r.team ? `${r.team}通算` : '通算'),
  round: r.round, league: r.league, team_id: 0, team_name: r.team, team_slug: r.team_slug,
  team_name_ja: r.team_name_ja, team_name_en: r.team_name_en, position: r.position,
  games: r.games, putouts: r.putouts, assists: r.assists, errors: r.errors,
  double_plays: r.doublePlays, fielding_percentage: r.fieldingPercentage, created_at: '', updated_at: '',
});

export const fetchTeamPlayers = async (
  teamSlug: string,
  signal?: AbortSignal,
): Promise<{ active: Profile[]; ob: Profile[]; team?: Partial<Team> }> => {
  try {
    const d = await apiFetch(`/api/teams/${teamSlug}/players?cv=20260222a`, signal);
    return {
      active: d.active.map(mapProfile),
      ob: d.ob.map(mapProfile),
      team: { id: d.team.teamId, slug: d.team.slug, official_name: d.team.officialName },
    };
  } catch (e) { if (e instanceof Error && e.name === 'AbortError') throw e; return { active: [], ob: [] }; }
};

export const fetchPlayer = async (
  idOrSlug: number | string,
  signal?: AbortSignal,
): Promise<Profile | undefined> => {
  try {
    const d = await apiFetch(`/api/players/${idOrSlug}`, signal);
    return mapProfile(d.player);
  } catch (e) {
    if (e instanceof Error && e.name === 'AbortError') throw e;
    return undefined;
  }
};

export const fetchAllPlayerStats = async (
  idOrSlug: number | string,
  signal?: AbortSignal,
): Promise<{
  batter: BatterStats[]; pitcher: PitcherStats[]; defense: DefenseStats[];
  awards: PlayerAward[]; npbAwards: PlayerNpbAward[];
  statcast?: StatcastSeasonRow[];
}> => {
  try {
    const d = await apiFetch(`/api/players/${idOrSlug}/stats`, signal);
    const pid = d.profileId;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const awards: PlayerAward[] = (d.awards ?? []).map((r: any) => ({
      yearId: r.yearId, awardId: r.awardId, awardNameJa: r.awardNameJa ?? null,
      awardNameEn: r.awardNameEn ?? null, awardNameEs: r.awardNameEs ?? null,
      awardCategory: r.awardCategory ?? null, league: r.league ?? null, position: r.position ?? null,
      tie: r.tie, notes: r.notes ?? null,
      periodType: r.periodType ?? null, periodLabel: r.periodLabel ?? null,
      periodStart: r.periodStart ?? null, periodEnd: r.periodEnd ?? null,
      sourceUrl: r.sourceUrl ?? null, playerName: r.playerName ?? null,
      teamName: r.teamName ?? null,
    }));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const npbAwards: PlayerNpbAward[] = (d.npbAwards ?? []).map((r: any) => ({
      year: r.year, awardId: r.awardId, awardNameJa: r.awardNameJa ?? null,
      awardNameEn: r.awardNameEn ?? null, awardNameEs: r.awardNameEs ?? null,
      awardCategory: r.awardCategory ?? null, league: r.league ?? null, position: r.position ?? null,
      periodType: r.periodType ?? null, periodLabel: r.periodLabel ?? null,
      periodStart: r.periodStart ?? null, periodEnd: r.periodEnd ?? null,
      division: r.division ?? null, teamName: r.teamName ?? null,
      notes: r.notes ?? null, sourceUrl: r.sourceUrl ?? null,
    }));
    return {
      batter: [
        ...d.batter.yearly.map((r: any) => mapBatter(r, pid)),
        ...d.batter.leagueCareer.map((r: any) => mapBatter(r, pid)),
        ...d.batter.career.map((r: any) => mapBatter(r, pid)),
        ...(d.batter.postseason ?? []).map((r: any) => mapBatter(r, pid)),
      ],
      pitcher: [
        ...d.pitcher.yearly.map((r: any) => mapPitcher(r, pid)),
        ...d.pitcher.leagueCareer.map((r: any) => mapPitcher(r, pid)),
        ...d.pitcher.career.map((r: any) => mapPitcher(r, pid)),
        ...(d.pitcher.postseason ?? []).map((r: any) => mapPitcher(r, pid)),
      ],
      defense: [
        ...d.defense.yearly.map((r: any) => mapDefense(r, pid)),
        ...d.defense.leagueCareer.map((r: any) => mapDefense(r, pid)),
        ...d.defense.career.map((r: any) => mapDefense(r, pid)),
        ...(d.defense.postseason ?? []).map((r: any) => mapDefense(r, pid)),
      ],
      awards, npbAwards,
      statcast: [...(d.batter.statcast ?? []), ...(d.pitcher.statcast ?? [])],
    };
  } catch (e) {
    if (e instanceof Error && e.name === 'AbortError') throw e;
    return { batter: [], pitcher: [], defense: [], awards: [], npbAwards: [] };
  }
};

export const fetchInternationalPlayerStats = async (
  idOrSlug: number | string,
  signal?: AbortSignal,
): Promise<InternationalPlayerStatsResponse> => {
  try {
    const d = await apiFetch(`/api/international/players/${idOrSlug}/stats`, signal);
    return {
      profileId: d.profileId ?? null,
      batting: Array.isArray(d.batting) ? d.batting : [],
      pitching: Array.isArray(d.pitching) ? d.pitching : [],
    };
  } catch (e) {
    if (e instanceof Error && e.name === 'AbortError') throw e;
    return { profileId: null, batting: [], pitching: [] };
  }
};

export const fetchInternationalCompetitions = async (
  signal?: AbortSignal,
): Promise<InternationalCompetition[]> => {
  try {
    const d = await apiFetch('/api/international/competitions', signal);
    return Array.isArray(d.competitions) ? d.competitions : [];
  } catch (e) {
    if (e instanceof Error && e.name === 'AbortError') throw e;
    return [];
  }
};

export const fetchInternationalTeams = async (
  signal?: AbortSignal,
): Promise<InternationalTeam[]> => {
  try {
    const d = await apiFetch('/api/international/teams', signal);
    return Array.isArray(d.teams) ? d.teams : [];
  } catch (e) {
    if (e instanceof Error && e.name === 'AbortError') throw e;
    return [];
  }
};

export const fetchInternationalCompetitionDetail = async (
  competitionId: string,
  signal?: AbortSignal,
): Promise<InternationalCompetitionDetail | null> => {
  try {
    const d = await apiFetch(`/api/international/competitions/${competitionId}`, signal);
    return {
      competition: d.competition ?? null,
      teams: Array.isArray(d.teams) ? d.teams : [],
    };
  } catch (e) {
    if (e instanceof Error && e.name === 'AbortError') throw e;
    return null;
  }
};

export const fetchInternationalCompetitionPlayerStats = async (
  competitionId: string,
  team?: string,
  signal?: AbortSignal,
): Promise<InternationalCompetitionPlayerStatsResponse> => {
  try {
    const q = new URLSearchParams();
    if (team) q.set('team', team);
    const suffix = q.toString() ? `?${q.toString()}` : '';
    const d = await apiFetch(`/api/international/competitions/${encodeURIComponent(competitionId)}/player-stats${suffix}`, signal);
    return {
      competition: d.competition ?? null,
      team: d.team ?? null,
      batting: Array.isArray(d.batting) ? d.batting : [],
      pitching: Array.isArray(d.pitching) ? d.pitching : [],
    };
  } catch (e) {
    if (e instanceof Error && e.name === 'AbortError') throw e;
    return { competition: null, team: null, batting: [], pitching: [] };
  }
};

export const fetchInternationalTeamStats = async (
  competitionId: string,
  team?: string,
  signal?: AbortSignal,
): Promise<InternationalTeamStatsRow[]> => {
  try {
    const q = new URLSearchParams();
    q.set('competition_id', competitionId);
    if (team) q.set('team', team);
    const d = await apiFetch(`/api/international/teams/stats?${q.toString()}`, signal);
    return Array.isArray(d.teamStats) ? d.teamStats : [];
  } catch (e) {
    if (e instanceof Error && e.name === 'AbortError') throw e;
    return [];
  }
};

export const fetchInternationalLeaders = async (
  competitionId: string,
  statGroup: 'batting' | 'pitching',
  statKey: string,
  limit = 10,
  signal?: AbortSignal,
): Promise<InternationalLeaderRow[]> => {
  try {
    const q = new URLSearchParams();
    q.set('competition_id', competitionId);
    q.set('stat_group', statGroup);
    q.set('stat_key', statKey);
    q.set('limit', String(limit));
    const d = await apiFetch(`/api/international/leaders?${q.toString()}`, signal);
    return Array.isArray(d.leaders) ? d.leaders : [];
  } catch (e) {
    if (e instanceof Error && e.name === 'AbortError') throw e;
    return [];
  }
};

export const fetchInternationalGames = async (
  params: { competitionId?: string; team?: string; stage?: string } = {},
  signal?: AbortSignal,
): Promise<InternationalGame[]> => {
  try {
    const q = new URLSearchParams();
    if (params.competitionId) q.set('competition_id', params.competitionId);
    if (params.team) q.set('team', params.team);
    if (params.stage) q.set('stage', params.stage);
    const suffix = q.toString() ? `?${q.toString()}` : '';
    const d = await apiFetch(`/api/international/games${suffix}`, signal);
    return Array.isArray(d.games) ? d.games : [];
  } catch (e) {
    if (e instanceof Error && e.name === 'AbortError') throw e;
    return [];
  }
};

export const fetchInternationalGameDetail = async (
  gameId: string,
  signal?: AbortSignal,
): Promise<InternationalGameStatsResponse | null> => {
  try {
    const d = await apiFetch(`/api/international/games/${encodeURIComponent(gameId)}`, signal);
    return {
      game: d.game ?? null,
      batting: Array.isArray(d.batting) ? d.batting : [],
      pitching: Array.isArray(d.pitching) ? d.pitching : [],
    };
  } catch (e) {
    if (e instanceof Error && e.name === 'AbortError') throw e;
    return null;
  }
};

export const fetchAwards = async (signal?: AbortSignal): Promise<AwardsResponse> => {
  try { return await apiFetch('/api/awards', signal); }
  catch (e) { if (e instanceof Error && e.name === 'AbortError') throw e; return { categories: [] }; }
};

export const fetchAwardWinners = async (
  awardId: string, year?: number, league?: string, signal?: AbortSignal,
): Promise<AwardWinnersResponse | null> => {
  try {
    const p = new URLSearchParams();
    if (year !== undefined) p.set('year', String(year));
    if (league !== undefined) p.set('league', league);
    const qs = p.toString() ? `?${p.toString()}` : '';
    return await apiFetch(`/api/awards/${encodeURIComponent(awardId)}/winners${qs}`, signal);
  } catch (e) { if (e instanceof Error && e.name === 'AbortError') throw e; return null; }
};

export const fetchNpbAwards = async (signal?: AbortSignal): Promise<NpbAwardsResponse> => {
  try { return await apiFetch('/api/npb-awards', signal); }
  catch (e) { if (e instanceof Error && e.name === 'AbortError') throw e; return { categories: [] }; }
};

export const fetchNpbAwardWinners = async (
  awardId: string, year?: number, league?: string, signal?: AbortSignal,
): Promise<NpbAwardWinnersResponse | null> => {
  try {
    const p = new URLSearchParams();
    if (year !== undefined) p.set('year', String(year));
    if (league !== undefined) p.set('league', league);
    const qs = p.toString() ? `?${p.toString()}` : '';
    return await apiFetch(`/api/npb-awards/${encodeURIComponent(awardId)}/winners${qs}`, signal);
  } catch (e) { if (e instanceof Error && e.name === 'AbortError') throw e; return null; }
};

export const fetchTraitRanking = async (
  traitId: string,
  threshold?: number,
  signal?: AbortSignal,
): Promise<TraitRankingResponse | null> => {
  try {
    const p = new URLSearchParams();
    if (threshold !== undefined) p.set('threshold', String(threshold));
    const qs = p.toString() ? `?${p.toString()}` : '';
    return await apiFetch(`/api/player-traits/${encodeURIComponent(traitId)}/ranking${qs}`, signal);
  } catch (e) {
    if (e instanceof Error && e.name === 'AbortError') throw e;
    return null;
  }
};

export const fetchPlayerTraits = async (
  slug: string,
  signal?: AbortSignal,
): Promise<PlayerTrait[]> => {
  try {
    const d = await apiFetch(`/api/players/${encodeURIComponent(slug)}/traits`, signal);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (d.traits ?? []).map((r: any): PlayerTrait => ({
      traitId: r.traitId,
      nameJa: r.nameJa,
      nameEn: r.nameEn ?? null,
      nameEs: r.nameEs ?? null,
      descriptionJa: r.descriptionJa ?? null,
      descriptionEn: r.descriptionEn ?? null,
      descriptionEs: r.descriptionEs ?? null,
      minDescriptionJa: r.minDescriptionJa ?? null,
      minDescriptionEn: r.minDescriptionEn ?? null,
      minDescriptionEs: r.minDescriptionEs ?? null,
      maxDescriptionJa: r.maxDescriptionJa ?? null,
      maxDescriptionEn: r.maxDescriptionEn ?? null,
      maxDescriptionEs: r.maxDescriptionEs ?? null,
      score: r.score ?? null,
      achievedCount: r.achievedCount ?? null,
      scoreUnitJa: r.scoreUnitJa ?? null,
      scoreUnitEn: r.scoreUnitEn ?? null,
      scoreUnitEs: r.scoreUnitEs ?? null,
      noteJa: r.noteJa ?? null,
      noteEn: r.noteEn ?? null,
      noteEs: r.noteEs ?? null,
      minValue: r.minValue ?? null,
      maxValue: r.maxValue ?? null,
      stepValue: r.stepValue ?? null,
      seasonMaxScore: r.seasonMaxScore ?? null,
      seasonThresholdCounts: r.seasonThresholdCounts ?? null,
      projectionThreshold: r.projectionThreshold ?? null,
      rankNo: r.rankNo ?? null,
      leagueScope: r.leagueScope,
      recordScope: r.recordScope,
      sortOrder: r.sortOrder ?? 0,
    }));
  } catch (e) {
    if (e instanceof Error && e.name === 'AbortError') throw e;
    return [];
  }
};
